import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { LinkIcon } from '@synoptic/ui-kit/icons/react/link.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef } from 'react';
import { StreamPostCardFragmentFragment } from '@/__generated__/graphql.ts';
import { PostCardMedia } from '@/components/post-card/post-card-media/post-card-media.tsx';
import { TextEditor } from '@/components/text-editor/text-editor.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { copyUrlToClipboard } from '@/utils/copy-url-to-clipboard.ts';
import {
  streamEventBodyV2,
  streamEventListV2,
  streamEventRowV2,
  streamEventShareV2,
  streamEventTextV2,
  streamEventTimeV2,
  streamEventTopicV2,
} from './stream-data-v2.css.ts';

const eventTime = (createdAt: string) => {
  const created = new Date(createdAt);
  return {
    date: created.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: created.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
      hour12: false,
    }),
  };
};

export const StreamEventList = ({
  className,
  ...props
}: ComponentPropsWithRef<'div'>) => (
  <div className={clsx(streamEventListV2, className)} {...props} />
);

export const StreamEventRowV2 = ({
  post,
}: {
  post: {
    id: string;
    text: string;
    createdAt: string;
    deletedAt?: string | null;
    authorId: string;
    media: StreamPostCardFragmentFragment['media'];
  };
}) => {
  const toast = useToast();
  const stamp = eventTime(post.createdAt);

  return (
    <article className={streamEventRowV2}>
      <div className={streamEventBodyV2}>
        {post.deletedAt ? (
          <p className={streamEventTextV2}>The post was deleted</p>
        ) : (
          <>
            <TextEditor
              namespace={`stream-event:${post.id}`}
              className={streamEventTextV2}
              initialState={post.text}
              format="md"
              editable={false}
            />
            <PostCardMedia
              media={post.media}
              authorId={post.authorId}
              createdAt={post.createdAt}
            />
          </>
        )}
      </div>
      <div className={streamEventTopicV2}>
        <time className={streamEventTimeV2} dateTime={post.createdAt}>
          <span>{stamp.date},</span>
          <span>{stamp.time}</span>
        </time>
        <ButtonV2
          type="button"
          variant="secondary"
          size="icon"
          aria-label="Share"
          className={streamEventShareV2}
          onClick={() => {
            copyUrlToClipboard(paths.makePost(post.id));
            toast.info({ title: 'Link copied to clipboard' });
          }}
        >
          <LinkIcon />
        </ButtonV2>
      </div>
    </article>
  );
};
